import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { DownOutlined } from '@ant-design/icons';
import { Tabs, Dropdown, Menu, message } from 'antd';
import PropertyDetailWrapper from '~components/property-detail-wrapper';
import enhanceForm from '~hocs/enhance-form';
import * as queries from '~settings/pms-queries';
import { propertyStatus } from '~constants/listing-management';
import VirtualTourLinks from '~pages/pms-dashboard/properties/listing-management/gallery-management/virtual-tour-links';
import FloorPlans from '~pages/pms-dashboard/properties/listing-management/gallery-management/floor-plan';
import SendForApprovalModal from '~pages/pms-dashboard/properties/listing-management/pending-approval/send-for-approval-modal';
import { cloneObject } from '~helpers';
import { listingManagementFetch } from '~helpers/graphql';
import { galleryCategories } from '~constants/gallery';
import { fireCustomEvent } from '~helpers/custom-events';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const { TabPane } = Tabs;
const PhotosAndVideos = React.lazy(() =>
  import('~pages/pms-dashboard/properties/listing-management/gallery-management/photos-and-videos'),
);

@enhanceForm()
export default class GalleryManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSendForApproval: false,
      statusFilter: props.statusFilter,
      activeKey: 'photosAndVideos',
      menuVisible: false,
    };
    this.isPublished = props.property.status === propertyStatus.PUBLISHED;
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  validateGotoTab = () => {
    const { activeKey } = this.state;
    const linksError = this.virtualTourLinks.isLinksError();
    const librariesError = this.photosAndVideos.isLibrariesError();

    if (linksError && !librariesError && activeKey === 'photosAndVideos') {
      this.setState({
        activeKey: 'virtualTourLinks',
      });
    }
    if (!linksError && librariesError && activeKey === 'virtualTourLinks') {
      this.setState({
        activeKey: 'photosAndVideos',
      });
    }
  };

  handleClickSave = () => {
    if (this.isSendApprovalFlow()) {
      if (!this.virtualTourLinks.isLinksError() && !this.photosAndVideos.isLibrariesError()) {
        this.setState({
          isSendForApproval: true,
        });

        return Promise.resolve({ isUpdated: false });
      }

      this.validateGotoTab();
      return Promise.resolve({ isUpdated: false });
    }

    return this.handleSubmit();
  };

  getUpdateGalleryInput = () => {
    const { property } = this.props;
    const propertyFloorsImages = this.floorPlans.getUpdateData();
    const { propertyId, propertyImages, propertyVideos, unitTypes } =
      this.photosAndVideos.getUpdateData();
    const { propertyLinks, unitLinks } = this.virtualTourLinks.getUpdateData();

    const updateGalleryInput = {
      propertyId,
      propertyImages,
      floorPlanImages: propertyFloorsImages.floorPlanImages,
      propertyVideos,
      propertyLinks,
      unitTypes,
    };

    unitLinks.map(unitLink => {
      updateGalleryInput.unitTypes.map(unit => {
        if (unitLink.unitTypeIdToLinkId?.includes(unit.unitTypeId)) {
          const updateUnit = property.unitTypes.edges?.find(
            propertyUnit => propertyUnit.node.id === unit.unitTypeId,
          );
          if (updateUnit && updateUnit.node) {
            const updateLink = updateUnit.node.allLinks?.find(
              propertyLink => propertyLink.link === unitLink.link,
            );
            const { displayRegion, link } = unitLink;

            if (updateLink) {
              unit.unitTypeLinks.push({
                displayRegion,
                id: updateLink.id,
                link,
              });
            } else {
              unit.unitTypeLinks.push({
                displayRegion,
                link,
              });
            }
          }

          return unit;
        }
        return true;
      });

      return true;
    });

    return updateGalleryInput;
  };

  handleSubmit = () => {
    if (this.virtualTourLinks.isLinksError() || this.photosAndVideos.isLibrariesError()) {
      this.validateGotoTab();

      return Promise.resolve({ isUpdated: false });
    }

    const updateGalleryInput = this.getUpdateGalleryInput();

    return listingManagementFetch(queries.updateGallery(updateGalleryInput))
      .then(res => {
        const updateGallery = res && res.updateGallery && res.updateGallery.gallery;

        fireCustomEvent('updateGallerySuccess');
        // reset changed status
        this.props.onSetUpdateGallery(false);
        if (this.isSendApprovalFlow()) {
          this.props.onSendApprovalGallerySuccess();
        }

        this.refreshGallery(updateGallery);
        this.getNewDrafts();

        return { isUpdated: true, isShowUpdateHint: true };
      })
      .catch(e => {
        message.error(`${this.props.t('cms:cms.message.error')}: ${e.message}`);
      });
  };

  getNewDrafts = () => {
    const { property } = this.props;
    listingManagementFetch(queries.getPropertyDrafts(property.id)).then(res => {
      const updateDrafts = res && res.drafts;

      this.props.setGalleryProperty({ property: { drafts: updateDrafts } });
    });
  };

  refreshGallery = updateGallery => {
    const { property } = this.props;
    const updatedProperty = cloneObject(property);

    if (updateGallery) {
      const { allLinks, allImages, allVideos, unitTypeGallerys } = updateGallery;
      updatedProperty.allLinks = allLinks;
      updatedProperty.allImages = allImages;
      updatedProperty.allVideos = allVideos;

      updatedProperty.unitTypes.edges.map(unit => {
        const unitTypeGallery = unitTypeGallerys.find(
          updateUnit => updateUnit.unitTypeId === unit.node.id,
        );
        Object.assign(unit.node, {
          allImages: unitTypeGallery.allImages,
          allVideos: unitTypeGallery.allVideos,
          allLinks: unitTypeGallery.allLinks.map(link => ({
            ...link,
            unitTypeId: decodeBase64(unit.node.id).id,
          })),
        });
        return unit;
      });
    }

    this.props.setGalleryProperty({ property: updatedProperty });
  };

  refreshAllImages = newImage => {
    const { property } = this.props;
    const updatedProperty = cloneObject(property);

    if (newImage) {
      const { category } = newImage;
      if (category === 'room') {
        const waitAddImageUnit = updatedProperty.unitTypes.edges.find(
          unit =>
            unit.node.id === btoa(JSON.stringify({ type: 'UnitType', id: newImage.unit_type_id })),
        );
        if (waitAddImageUnit) {
          waitAddImageUnit.node.allImages.edges.push({
            node: Object.assign(newImage, {
              category: galleryCategories[newImage.category],
              contentType: newImage.content_type,
            }),
          });
        }
      } else {
        updatedProperty.allImages.edges.push({
          node: Object.assign(newImage, {
            category: galleryCategories[newImage.category],
            contentType: newImage.content_type,
          }),
        });
      }
    }

    this.props.setGalleryProperty({ property: updatedProperty });
  };

  getWaitingApprovalData = () => {
    if (!this.photosAndVideos || !this.virtualTourLinks) {
      return {
        images: [],
        videos: [],
        links: [],
      };
    }

    const imageAndVideo = this.photosAndVideos.getWaitingApproval();
    const links = this.virtualTourLinks.getWaitingApproval();

    return { ...imageAndVideo, ...links };
  };

  handleCloseModal = () => {
    this.setState({
      isSendForApproval: false,
    });
  };

  handleClickStatusOption = statusOption => {
    this.setState({
      statusFilter: statusOption,
    });
  };

  getFilterMenu = () => (
    <Menu>
      <For
        each="option"
        of={['all_content', 'draft', 'pending_approval', 'published', 'rejected']}
        index="index"
      >
        <Menu.Item
          key={index}
          className={
            this.state.statusFilter === option ? 'gallery-management__selected-option' : ''
          }
        >
          <span
            role="presentation"
            className="gallery-management__status-option"
            onClick={() => {
              this.handleClickStatusOption(option);
              this.setState({ menuVisible: false });
            }}
          >
            {this.props.t(`cms:cms.gallery.filter_menu.item.${option}`)}
          </span>
        </Menu.Item>
      </For>
    </Menu>
  );

  isSendApprovalFlow = () => {
    const waitingApprovalData = this.getWaitingApprovalData();
    const { images, videos, links } = waitingApprovalData;

    return this.isPublished && (images.length > 0 || videos.length > 0 || links.length > 0);
  };

  handleChangeActiveTab = activeKey => {
    this.setState({
      activeKey,
    });
  };

  handleChangeVisible = visible => {
    this.setState({
      menuVisible: visible,
    });
  };

  render() {
    const { t, property, isHidden } = this.props;
    const { isSendForApproval, statusFilter, activeKey, menuVisible } = this.state;
    const waitingApprovalData = this.getWaitingApprovalData();

    return (
      <PropertyDetailWrapper
        t={t}
        isPublished={this.isPublished}
        onClickSave={this.handleClickSave}
        isHidden={isHidden}
        draft={this.props.draft}
        isPendingApprovalSection
        property={property}
      >
        <div className="gallery-management">
          <Dropdown
            overlay={this.getFilterMenu()}
            placement="bottomRight"
            visible={menuVisible}
            onVisibleChange={this.handleChangeVisible}
          >
            <span className="gallery-management__status-filter">
              {t(`cms:cms.gallery.filter_menu.item.${statusFilter}`)}
              <DownOutlined
                className={classNames('gallery-management__down-icon', {
                  'gallery-management__down-icon--menu-visible': menuVisible,
                })}
              />
            </span>
          </Dropdown>
          <Tabs activeKey={activeKey} onChange={this.handleChangeActiveTab}>
            <TabPane
              key="photosAndVideos"
              tab={t('cms:cms.property.listing_management.photos_and_videos.tab')}
            >
              <React.Suspense fallback="">
                <PhotosAndVideos
                  t={t}
                  property={property}
                  onGotoRoomConfig={this.props.onGotoRoomConfig}
                  onRef={node => {
                    this.photosAndVideos = node;
                  }}
                  onSetUpdateGallery={this.props.onSetUpdateGallery}
                  statusFilter={statusFilter}
                  onSetStatusOption={this.handleClickStatusOption}
                  setProperty={this.props.setProperty}
                />
              </React.Suspense>
            </TabPane>
            <TabPane
              key="virtualTourLinks"
              tab={t('cms:cms.property.listing_management.virtual_tour_links.tab')}
              forceRender
            >
              <VirtualTourLinks
                t={t}
                property={property}
                onSetUpdateGallery={this.props.onSetUpdateGallery}
                onRef={node => {
                  this.virtualTourLinks = node;
                }}
                statusFilter={statusFilter}
                onGotoRoomConfig={this.props.onGotoRoomConfig}
              />
            </TabPane>
            <TabPane
              key="floorPlan"
              tab={t('cms:cms.property.listing_management.floor_plan.tab')}
              forceRender
            >
              <FloorPlans
                t={t}
                property={property}
                onRef={node => {
                  this.floorPlans = node;
                }}
              />
            </TabPane>
          </Tabs>
        </div>
        <If condition={isSendForApproval}>
          <SendForApprovalModal
            t={t}
            activeModal={isSendForApproval}
            waitingApprovalData={waitingApprovalData}
            onCloseModal={this.handleCloseModal}
            onSendForApproval={this.handleSubmit}
          />
        </If>
      </PropertyDetailWrapper>
    );
  }
}

GalleryManagement.propTypes = {
  t: PropTypes.func.isRequired,
  property: PropTypes.object,
  onRef: PropTypes.func.isRequired,
  onGotoRoomConfig: PropTypes.func,
  setGalleryProperty: PropTypes.func,
  onSetUpdateGallery: PropTypes.func,
  isHidden: PropTypes.bool,
  onSendApprovalGallerySuccess: PropTypes.func,
  statusFilter: PropTypes.oneOf(['all_content', 'pending_approval', 'published', 'rejected']),
  draft: PropTypes.any,
  setProperty: PropTypes.func,
};

GalleryManagement.defaultProps = {
  t: () => {},
  property: {},
  onRef: () => {},
  onGotoRoomConfig: () => {},
  setGalleryProperty: () => {},
  onSetUpdateGallery: () => {},
  isHidden: false,
  onSendApprovalGallerySuccess: () => {},
  statusFilter: 'all_content',
  draft: null,
  setProperty: () => {},
};
